package fyi.moko.hotpl.ui.directory

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.data.location.CurrentLocationProvider
import fyi.moko.hotpl.data.location.LocationContextResolver
import fyi.moko.hotpl.data.service.SyncCoordinator
import kotlin.time.Clock
import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.inferVisit
import fyi.moko.hotpl.domain.model.GeohashEncoder
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.model.YellowPageEntry
import fyi.moko.hotpl.domain.model.SearchPrediction
import fyi.moko.hotpl.domain.repository.YellowPageRepository
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.DestinationHistoryStore
import fyi.moko.hotpl.domain.service.PlaceSearchProvider
import fyi.moko.hotpl.domain.usecase.SavePlaceRejection
import fyi.moko.hotpl.domain.usecase.SavePlaceUseCase
import fyi.moko.hotpl.domain.usecase.SavePlaceResult
import io.ktor.client.plugins.ClientRequestException
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * ViewModel for the Directory (Yellow Pages) tab.
 *
 * Responsibilities:
 * 1. Sync the nearest region slice from the remote directory cache on init.
 * 2. Observe an offline name-prefix search over the local cache.
 * 3. Import a selected directory entry into the user's personal journal.
 */
class DirectoryViewModel(
    private val yellowPageRepository: YellowPageRepository,
    private val savePlaceUseCase: SavePlaceUseCase,
    private val authService: AuthService,
    private val currentLocationProvider: CurrentLocationProvider,
    private val placeSearchProvider: PlaceSearchProvider,
    private val syncCoordinator: SyncCoordinator,
    private val destinationHistoryStore: DestinationHistoryStore,
    private val locationContextResolver: LocationContextResolver,
) : ViewModel() {

    private val _searchQuery = MutableStateFlow("")
    val searchQuery: StateFlow<String> = _searchQuery.asStateFlow()

    /**
     * Optional free-text destination area (e.g. "Culver City", "LA Koreatown") the user
     * is searching near instead of their current GPS location — e.g. planning a visit to
     * a different neighborhood. When set, it's appended to the remote query text and the
     * device-location geohash bias is dropped, since biasing to the current location would
     * fight a destination that's actually somewhere else; Google's own text relevance
     * ranks the combined query instead.
     */
    private val _destinationKeyword = MutableStateFlow("")
    val destinationKeyword: StateFlow<String> = _destinationKeyword.asStateFlow()

    /** Most-recent-first destination areas the user has previously searched near. */
    val recentDestinations: StateFlow<List<String>> = destinationHistoryStore.recentDestinations

    private val _directoryEntries = MutableStateFlow<List<YellowPageEntry>>(emptyList())
    val directoryEntries: StateFlow<List<YellowPageEntry>> = _directoryEntries.asStateFlow()

    /** Region sync now runs on [SyncCoordinator]'s connectivity-gated retry loop (F-11). */
    val isSyncing: StateFlow<Boolean> = syncCoordinator.isSyncingRegion

    private val _importInProgress = MutableStateFlow<Set<String>>(emptySet())
    val importInProgress: StateFlow<Set<String>> = _importInProgress.asStateFlow()

    private val _uiMessage = MutableStateFlow<String?>(null)
    val uiMessage: StateFlow<String?> = _uiMessage.asStateFlow()

    /**
     * Live autocomplete candidates surfaced as a remote fallback. Named for the original
     * Google-only implementation; the active [placeSearchProvider] may now be the free
     * Nominatim one instead — see [onlineResultsAttribution].
     */
    val googlePredictions = MutableStateFlow<List<SearchPrediction>>(emptyList())

    /**
     * Attribution the UI must display alongside [googlePredictions], if whichever
     * provider actually produced them requires one (e.g. OSM's ODbL license via
     * Nominatim). Re-read after every search rather than cached once at construction —
     * [placeSearchProvider] may be a [fyi.moko.hotpl.domain.service.FallbackPlaceSearchProvider]
     * whose `attributionText` depends on which of its two providers served the last call.
     */
    private val _onlineResultsAttribution = MutableStateFlow<String?>(null)
    val onlineResultsAttribution: StateFlow<String?> = _onlineResultsAttribution.asStateFlow()

    /** True while a remote autocomplete request is in flight. */
    val isSearchingRemote = MutableStateFlow(false)

    /**
     * Whether [googlePredictions] currently holds a distance-ranked result set (an explicit
     * submitted search or a "near me" browse) rather than typeahead suggestions. The list
     * looks the same either way, so the header has to say which promise is being made.
     */
    private val _resultsAreNearestFirst = MutableStateFlow(false)
    val resultsAreNearestFirst: StateFlow<Boolean> = _resultsAreNearestFirst.asStateFlow()

    /**
     * Which kind of place the current nearby browse asked for; null means "anything".
     * An unfiltered browse mixes a grocery store in with the cafe next door, which reads as
     * noise — the same complaint the Journal's category chips answer, one screen over.
     */
    private val _selectedNearbyCategory = MutableStateFlow<PlaceCategory?>(null)
    val selectedNearbyCategory: StateFlow<PlaceCategory?> = _selectedNearbyCategory.asStateFlow()

    /**
     * "Near me"와 카테고리 칩을 화면에 낼지. 지금 붙어 있는 검색 제공자가 쿼리 없는 탐색을
     * 할 수 있을 때만 낸다 — 자세한 이유는 [PlaceSearchProvider.supportsNearbyBrowse].
     *
     * 상태가 아니라 값인 이유: 제공자는 Koin이 한 번 정해 주고 실행 중에 바뀌지 않는다.
     */
    val nearbyBrowseAvailable: Boolean = placeSearchProvider.supportsNearbyBrowse

    /**
     * In-memory LRU cache of recent autocomplete responses, keyed by `"$query|$regionKey"`.
     * Backspace-then-retype of the same query is common and would otherwise burn another
     * billable+quota Autocomplete request for an identical result. Capped at
     * [MAX_AUTOCOMPLETE_CACHE_ENTRIES]; cleared when the query goes blank. Attribution is
     * cached alongside the predictions since a [fyi.moko.hotpl.domain.service.FallbackPlaceSearchProvider]
     * can answer different queries from different underlying providers.
     */
    private data class CachedSearch(val predictions: List<SearchPrediction>, val attribution: String?)

    private val autocompleteCache: MutableMap<String, CachedSearch> = LinkedHashMap()

    private fun cacheAutocompleteResult(key: String, predictions: List<SearchPrediction>, attribution: String?) {
        autocompleteCache[key] = CachedSearch(predictions, attribution)
        while (autocompleteCache.size > MAX_AUTOCOMPLETE_CACHE_ENTRIES) {
            val iterator = autocompleteCache.entries.iterator()
            if (iterator.hasNext()) {
                iterator.next()
                iterator.remove()
            } else break
        }
    }

    private val _currentRegionKey = MutableStateFlow(FALLBACK_REGION_KEY)

    /**
     * The searching user's exact location, used to anchor remote autocomplete and to give
     * each prediction a distance. Deliberately separate from [_currentRegionKey]: that one
     * is a coarse Firestore cache partition (5 geohash chars ≈ a 5 km cell) and is far too
     * blunt to decide which branch of a chain is nearest.
     */
    private val currentOrigin = MutableStateFlow<GeoPoint?>(null)

    /** Surfaced so the screen can say what every distance here is measured from. */
    val origin: StateFlow<GeoPoint?> = currentOrigin.asStateFlow()

    init {
        syncNearestRegion()

        // collectLatest (not onEach + launch): a newly settled query cancels the previous
        // search — including its in-flight Google Autocomplete request — so a slow stale
        // response can never overwrite predictions for the current query, and abandoned
        // queries stop costing billed API calls. syncCoordinator.regionSyncTick re-runs the
        // current query when the region sync (F-11) lands after the user already searched.
        viewModelScope.launch {
            combine(
                _searchQuery.debounce(SEARCH_DEBOUNCE_MS).distinctUntilChanged(),
                _destinationKeyword.debounce(SEARCH_DEBOUNCE_MS).distinctUntilChanged(),
                syncCoordinator.regionSyncTick,
            ) { query, destination, _ -> query to destination }
                .collectLatest { (query, destination) ->
                    val trimmed = query.trim()
                    val results = if (trimmed.isBlank()) {
                        emptyList()
                    } else {
                        yellowPageRepository.searchLocal(prefix = trimmed.lowercase())
                    }
                    _directoryEntries.value = results

                    if (trimmed.isBlank()) {
                        // Clear the stale-response cache so a fresh search starts clean.
                        autocompleteCache.clear()
                        // ...but an emptied query must not wipe a "near me" browse, which
                        // deliberately runs with no query and owns the list while it shows.
                        if (!_resultsAreNearestFirst.value) {
                            googlePredictions.value = emptyList()
                            _onlineResultsAttribution.value = null
                        }
                    } else if (trimmed.length >= MIN_REMOTE_QUERY_LENGTH && results.size < REMOTE_SEARCH_THRESHOLD) {
                        val destinationKeyword = destination.trim()
                        // Autocomplete is a typeahead, not a proximity query: it returns five
                        // predictions ranked by prominence, so searching a chain from home
                        // listed branches 4 km away and left out the one 460 m down the road.
                        // The distance-ranked endpoint runs here instead, once the query has
                        // settled. It costs more per call, which is why it waits for the
                        // debounce and reuses the same response cache.
                        val origin = if (destinationKeyword.isBlank()) originForSearch() else null
                        if (origin != null) {
                            performNearestSearch(trimmed, origin)
                        } else {
                            // No fix yet, or the user named a destination they are not
                            // standing in — distance ranking would be meaningless either way.
                            searchRemote(trimmed, destinationKeyword)
                        }
                    } else {
                        googlePredictions.value = emptyList()
                        _onlineResultsAttribution.value = null
                    }
                }
        }
    }

    fun onSearchQueryChange(value: String) {
        _searchQuery.value = value
        // Typing again is a return to typeahead; the nearest-first promise no longer holds.
        _resultsAreNearestFirst.value = false
        _selectedNearbyCategory.value = null
    }

    fun onDestinationKeywordChange(value: String) {
        _destinationKeyword.value = value
        _resultsAreNearestFirst.value = false
    }

    /**
     * Runs the submitted query as a distance-ranked search (the field's IME "search" action).
     *
     * The debounced typeahead already runs this same search once a query settles, so submit
     * is now a way to re-run it deliberately (after a location fix lands, say) rather than
     * the only way to reach distance ranking.
     */
    fun onSearchSubmitted() {
        val query = _searchQuery.value.trim()
        if (query.isBlank()) return
        runNearestSearch(query)
    }

    /**
     * "Near me": what is around here right now, with no query at all — optionally narrowed
     * to one [category]. Tapping the category that is already showing takes the filter back
     * off rather than re-running the same search.
     */
    fun browseNearby(category: PlaceCategory? = null) {
        val next = if (_resultsAreNearestFirst.value && category == _selectedNearbyCategory.value) {
            null
        } else {
            category
        }
        _selectedNearbyCategory.value = next
        // Set before clearing the query so the debounced typeahead collector, which fires a
        // moment later on the now-blank query, leaves these results alone.
        _resultsAreNearestFirst.value = true
        _searchQuery.value = ""
        runNearestSearch(query = "", category = next)
    }

    private fun runNearestSearch(query: String, category: PlaceCategory? = null) {
        viewModelScope.launch {
            val origin = originForSearch()
            if (origin == null) {
                _resultsAreNearestFirst.value = false
                _uiMessage.value = "Location unavailable — can't rank results by distance"
                return@launch
            }
            performNearestSearch(query, origin, category)
        }
    }

    /**
     * The distance-ranked search itself, against a caller-resolved [origin].
     *
     * Suspends rather than launching its own coroutine so the debounced typeahead can call
     * it from inside `collectLatest`: a newer keystroke then cancels the in-flight request
     * instead of racing it, which is what keeps a slow stale response from overwriting the
     * current query's results — and stops abandoned queries from being billed.
     */
    private suspend fun performNearestSearch(
        query: String,
        origin: GeoPoint,
        category: PlaceCategory? = null,
    ) {
        // Namespaced apart from the autocomplete entries: the two endpoints answer the same
        // query differently, and serving one where the other is expected is the whole bug.
        val cacheKey = "nearest|$query|${category?.name}|" +
            "${origin.latitude.roundedForCacheKey()},${origin.longitude.roundedForCacheKey()}"
        val cached = autocompleteCache[cacheKey] ?: run {
            isSearchingRemote.value = true
            try {
                val fetched = placeSearchProvider
                    .searchNearest(query, origin, category)
                    .sortedWith(compareBy(nullsLast()) { it.distanceMeters })
                // Read after the call: a FallbackPlaceSearchProvider's attributionText
                // depends on which of its two providers just served this query.
                CachedSearch(fetched, placeSearchProvider.attributionText)
                    .also { cacheAutocompleteResult(cacheKey, it.predictions, it.attribution) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("DirectoryViewModel: nearest search failed: ${e.message ?: e::class.simpleName}")
                _resultsAreNearestFirst.value = false
                _uiMessage.value = if (isQuotaExceeded(e)) {
                    "Search quota reached for today"
                } else {
                    "Nearby search unavailable right now"
                }
                return
            } finally {
                isSearchingRemote.value = false
            }
        }
        googlePredictions.value = cached.predictions
        _onlineResultsAttribution.value = cached.attribution
        _resultsAreNearestFirst.value = true
        if (cached.predictions.isEmpty()) {
            _uiMessage.value = if (query.isBlank()) {
                val what = category?.label?.lowercase() ?: "place"
                "No " + what + " found within walking distance"
            } else {
                "No nearby match for \"$query\""
            }
        }
    }

    /**
     * Records the current destination keyword into [recentDestinations]. Called on an
     * explicit commit (the field's IME "search" action) rather than on every keystroke —
     * recording each debounced-but-still-mid-typing value would spam the history with
     * partial strings.
     */
    fun onDestinationSubmitted() {
        viewModelScope.launch {
            destinationHistoryStore.record(_destinationKeyword.value)
        }
    }

    fun clearUiMessage() {
        _uiMessage.value = null
    }

    /**
     * Imports a directory entry into the user's private journal.
     * The entry's [canonicalId] (Google Place ID) is intentionally not reused as the
     * place id: the journal place needs its own client-generated UUID because it is
     * a private record, not the shared directory document.
     */
    @OptIn(ExperimentalUuidApi::class)
    fun importToJournal(
        entry: YellowPageEntry,
        onSuccess: (placeId: String) -> Unit = {},
        onError: (String) -> Unit = {},
    ) {
        viewModelScope.launch {
            _importInProgress.update { it + entry.entryId }
            try {
                handleSaveResult(saveEntryToJournal(entry), onSuccess, onError)
            } catch (e: Exception) {
                val message = e.message ?: "Failed to import place"
                _uiMessage.value = message
                onError(message)
            } finally {
                _importInProgress.update { it - entry.entryId }
            }
        }
    }

    /**
     * Imports a Google autocomplete prediction into the journal and also submits it
     * to the directory moderation queue so other users can benefit from it later.
     */
    fun importGooglePlace(
        prediction: SearchPrediction,
        onSuccess: (placeId: String) -> Unit = {},
        onError: (String) -> Unit = {},
    ) {
        viewModelScope.launch {
            _importInProgress.update { it + prediction.placeId }
            try {
                val entry = placeSearchProvider.fetchPlaceDetails(prediction.placeId)
                // Local-first: the user's journal save must not wait on — or be aborted
                // by — the community contribution, which is a best-effort Firestore write.
                val result = saveEntryToJournal(entry)
                yellowPageRepository.submitPendingEntry(entry).onFailure { e ->
                    println("DirectoryViewModel: pending entry submit failed: ${e.message ?: e::class.simpleName}")
                }
                handleSaveResult(result, onSuccess, onError)
            } catch (e: Exception) {
                val message = e.message ?: "Failed to import Google place"
                _uiMessage.value = message
                onError(message)
            } finally {
                _importInProgress.update { it - prediction.placeId }
            }
        }
    }

    private suspend fun saveEntryToJournal(entry: YellowPageEntry): SavePlaceResult {
        val ownerId = authService.currentUserId.first() ?: ""
        return savePlaceUseCase(entry.toJournalPlace(ownerId), saveAnyway = true)
    }

    /**
     * [onSuccess] carries the journal place's own id (not the directory entry id) so the
     * screen can open it — 리뷰어 요청 0-24: "담는다"와 "기록한다"가 한 동작이길 원한다.
     */
    private fun handleSaveResult(
        result: SavePlaceResult,
        onSuccess: (placeId: String) -> Unit,
        onError: (String) -> Unit,
    ) {
        when (result) {
            is SavePlaceResult.Saved -> {
                _uiMessage.value = "Saved to journal"
                onSuccess(result.id)
            }
            SavePlaceResult.DuplicateNameWarning -> {
                // saveAnyway = true should prevent this; there is no id to open, so only
                // the message is shown.
                _uiMessage.value = "Already in journal"
            }
            is SavePlaceResult.Rejected -> {
                val message = when (result.reason) {
                    SavePlaceRejection.EmptyName -> "Name is required"
                }
                _uiMessage.value = message
                onError(message)
            }
        }
    }

    private suspend fun searchRemote(query: String, destinationKeyword: String) {
        val combinedQuery = if (destinationKeyword.isNotBlank()) "$query $destinationKeyword" else query
        // An explicit destination overrides the device-location anchor entirely (see
        // _destinationKeyword) rather than combining both, which could exclude the
        // very place names the user is asking about.
        val origin = if (destinationKeyword.isBlank()) originForSearch() else null
        // Rounded into the cache key so tiny GPS jitter doesn't miss the cache on every
        // keystroke; ~3 decimal places is roughly 100 m, far finer than the bias circle.
        val originKey = origin?.let { "${it.latitude.roundedForCacheKey()},${it.longitude.roundedForCacheKey()}" }
        val cacheKey = "$combinedQuery|$originKey"
        // A cache hit skips a billable+quota API call for a query we already answered
        // (e.g. backspace-then-retype within the same session).
        val cached = autocompleteCache[cacheKey]
        if (cached != null) {
            googlePredictions.value = cached.predictions
            _onlineResultsAttribution.value = cached.attribution
            return
        }
        isSearchingRemote.value = true
        try {
            val remote = placeSearchProvider
                .searchAutocomplete(query = combinedQuery, origin = origin)
                .sortedWith(compareBy(nullsLast()) { it.distanceMeters })
            // Read after the call, not before — a FallbackPlaceSearchProvider's
            // attributionText depends on which of its two providers just served this query.
            val attribution = placeSearchProvider.attributionText
            cacheAutocompleteResult(cacheKey, remote, attribution)
            googlePredictions.value = remote
            _onlineResultsAttribution.value = attribution
        } catch (e: CancellationException) {
            // A newer query superseded this one (collectLatest) — must propagate, not be
            // swallowed as a network failure.
            throw e
        } catch (e: Exception) {
            // Remote search is a best-effort fallback; failures leave the UI on local
            // results — but tell the user, or an exhausted API quota (HTTP 429) looks
            // exactly like "search is broken".
            println("DirectoryViewModel: remote search failed: ${e.message ?: e::class.simpleName}")
            googlePredictions.value = emptyList()
            _onlineResultsAttribution.value = null
            _uiMessage.value = if (isQuotaExceeded(e)) {
                "Google search quota reached for today — showing saved places only"
            } else {
                "Online search unavailable — showing saved places only"
            }
        } finally {
            isSearchingRemote.value = false
        }
    }

    private fun isQuotaExceeded(e: Exception): Boolean =
        (e as? ClientRequestException)?.response?.status == HttpStatusCode.TooManyRequests

    private fun syncNearestRegion() {
        viewModelScope.launch {
            val regionKey = resolveRegionKey()
            _currentRegionKey.value = regionKey
            // Delegates to SyncCoordinator's connectivity-gated retry/backoff worker (F-11)
            // instead of a single unmanaged attempt here.
            syncCoordinator.requestRegionSync(regionKey)
        }
    }

    /**
     * The user's exact location, re-read whenever it isn't known yet. The previous code
     * resolved location once in `init` and never again, so granting the location permission
     * after the tab had already been opened left every later search unanchored.
     */
    private suspend fun refreshOrigin(): GeoPoint? =
        currentLocationProvider.lastKnownFix()?.let { fix ->
            originFixAgeMillis = fix.ageMillis
            fix.point.also { currentOrigin.value = it }
        }

    /** 좌표의 나이. 방문 추론이 낡은 좌표로 "안 가본 곳"을 만들지 않도록 같이 들고 다닌다. */
    private var originFixAgeMillis: Long? = null

    private suspend fun originForSearch(): GeoPoint? = currentOrigin.value ?: refreshOrigin()

    private suspend fun resolveRegionKey(): String {
        val location = refreshOrigin()
        val geohash = if (location != null) {
            GeohashEncoder.encode(location.latitude, location.longitude, precision = GEOHASH_PRECISION)
        } else {
            FALLBACK_REGION_KEY
        }
        return geohash.take(REGION_KEY_LENGTH)
    }

    @OptIn(ExperimentalUuidApi::class)
    private suspend fun YellowPageEntry.toJournalPlace(ownerId: String): Place = Place(
        id = Uuid.random().toString(),
        name = name,
        latitude = latitude,
        longitude = longitude,
        tags = tags,
        // Real reverse-geocoded address, same resolver map-tap place creation uses —
        // previously this field was overwritten with "Tel: ...\nWeb: ..." text, discarding
        // the address entirely (Directory-import address gap fix).
        locationContext = locationContextResolver.resolve(latitude, longitude),
        phoneNumber = phoneNumber?.trim()?.ifBlank { null },
        website = website?.trim()?.ifBlank { null },
        // 디렉터리에서 담는 장소는 대개 **아직 안 가본 곳**이다. 예전에는 여기가
        // VISITED로 못박혀 있어서, 검색으로 찾기만 한 곳도 "가봤음"으로 저장됐다.
        // 지금 그 자리에 서 있다면(가게에서 검색해 담는 경우) 추론이 VISITED로 돌린다.
        status = inferVisit(
            place = latitude?.let { lat -> longitude?.let { lng -> GeoPoint(lat, lng) } },
            photos = emptyList(),
            currentLocation = currentOrigin.value,
            fixAgeMillis = originFixAgeMillis,
            // viewModelOf(::…)는 생성자 인자를 전부 Koin에서 찾는다 — 기본값 있는 시계를
            // 넣으면 인스턴스 생성이 실패한다. 그래서 여기서는 직접 읽는다.
            now = Clock.System.now().toEpochMilliseconds(),
        ).status,
        visibility = Visibility.PRIVATE,
        // "담았다"와 "다시 가고 싶다"는 다른 뜻이다. 예전 기본값 true는 한 번 방문하는 순간 담은
        // 장소 전부에 Revisit soon 배지를 붙이고 회상 카드 후보로 만들었다(2026-09-17).
        liked = false,
        ownerId = ownerId,
    )

    /** 3 decimal places ≈ 100 m — enough to key a cache without chasing GPS jitter. */
    private fun Double.roundedForCacheKey(): Long = (this * 1000).toLong()

    companion object {
        private const val SEARCH_DEBOUNCE_MS = 300L
        // Single source of truth for geohash precision / region-key length lives in
        // YellowPageEntry so region sync and the Google import path can never diverge.
        private const val GEOHASH_PRECISION = YellowPageEntry.GEOHASH_PRECISION
        private const val REGION_KEY_LENGTH = YellowPageEntry.REGION_KEY_LENGTH

        /** LA Koreatown coarse geohash prefix, used when location is unavailable. */
        private const val FALLBACK_REGION_KEY = "9q5ct"

        /** Minimum number of local results that prevents a remote autocomplete fallback. */
        private const val REMOTE_SEARCH_THRESHOLD = 3

        /**
         * Minimum query length (characters) before the remote autocomplete fallback
         * fires. 1-2 character queries are low-signal and waste billable+quota API calls.
         */
        private const val MIN_REMOTE_QUERY_LENGTH = 3

        /** Max entries in the in-memory autocomplete response cache (LRU eviction). */
        private const val MAX_AUTOCOMPLETE_CACHE_ENTRIES = 20
    }
}
